import React from "react";
import { PlusIcon, Trash2Icon } from "lucide-react";

import type { getCart, removeCartItemById, Schemas } from "@fluid-app/fluid";
import { Button, ScrollArea } from "@fluid-app/ui";

import type { dispatchCartEvent } from "../utils";
import type { ErrorBannerType } from "./ErrorBanner";
import { QuantityModifier } from "../components/QuantityModifier";
import CartSubscriptions from "./CartSubscriptions";
import ErrorBanner from "./ErrorBanner";

export type CartData = Awaited<ReturnType<typeof getCart>>;

export const getTotalQuantityFromCart = (cart: CartData): number => {
  if (!cart?.items) return 0;
  return cart.items.reduce(
    (sum, item) => sum + (Number(item.quantity) || 0),
    0,
  );
};

export const isEnrollmentPackItem = (item: Schemas["CartItem"]): boolean => {
  return Boolean(item.enrollment);
};

interface EnrollmentPack {
  id: string | number;
  title: string;
  price: string;
  items: Schemas["CartItem"][];
  firstItem: Schemas["CartItem"];
}

export const groupCartItems = (
  items: Schemas["CartItem"][] | undefined,
  cart: CartData,
) => {
  if (!items || items.length === 0)
    return { regularItems: [], enrollmentPacks: [] };

  const allItems = [...items];
  const enrollmentItems = allItems.filter(isEnrollmentPackItem);
  const regularItems = allItems.filter((item) => !isEnrollmentPackItem(item));

  const enrollmentPacks: EnrollmentPack[] = [];

  if (enrollmentItems.length === 0) {
    return { regularItems, enrollmentPacks };
  }

  if (cart?.enrollment_pack && enrollmentItems.length > 0) {
    const firstItem = enrollmentItems[0];
    if (!firstItem) {
      return { regularItems, enrollmentPacks };
    }
    const pack = {
      id: cart.enrollment_pack.enrollment_pack_id ?? "enrollment-pack",
      title: cart.enrollment_pack.title ?? "Enrollment Pack",
      price: cart.enrollment_pack.price_in_currency ?? "0",
      items: enrollmentItems,
      firstItem,
    };
    enrollmentPacks.push(pack);
  } else {
    const defaultItem = enrollmentItems[0];
    if (defaultItem) {
      const pack = {
        id: "enrollment-pack",
        title: defaultItem.product_title
          ? `${defaultItem.product_title} Pack`
          : "Enrollment Pack",
        price: "0",
        items: enrollmentItems,
        firstItem: defaultItem,
      };
      enrollmentPacks.push(pack);
    }
  }
  return { regularItems, enrollmentPacks };
};

export interface CartSidebarContentProps {
  effectiveShopDisplayName: string;
  handleCheckout: () => void;
  isCartLoading: boolean;
  isCheckoutPriceLoading: boolean;
  isAnyItemLoading: boolean;
  cart: CartData;
  itemLoadingStates: Record<number, boolean>;
  demoMode: boolean;
  setItemLoadingStates: React.Dispatch<
    React.SetStateAction<Record<number, boolean>>
  >;
  setCart: React.Dispatch<React.SetStateAction<CartData>>;
  setItemCount: React.Dispatch<React.SetStateAction<number>>;
  getTotalQuantityFromCart: typeof getTotalQuantityFromCart;
  removeCartItemById: typeof removeCartItemById;
  getCartWithFallback: () => Promise<CartData>;
  dispatchCartEvent: typeof dispatchCartEvent;
  widgetIdRef: React.MutableRefObject<string>;
  handleIncrement: (variantId: number) => Promise<void>;
  handleDecrement: (variantId: number) => Promise<void>;
  setIsCheckoutPriceLoading: React.Dispatch<React.SetStateAction<boolean>>;
  onRemoveEnrollmentPack?: () => void;
  onEnrollmentPackAdd?: () => void;
  alwaysOpen?: boolean;
}

const CartSidebarContent: React.FC<CartSidebarContentProps> = ({
  effectiveShopDisplayName,
  handleCheckout,
  isCartLoading,
  isCheckoutPriceLoading,
  isAnyItemLoading,
  cart,
  itemLoadingStates,
  demoMode,
  setItemLoadingStates,
  setCart,
  setItemCount,
  getTotalQuantityFromCart,
  removeCartItemById,
  getCartWithFallback,
  dispatchCartEvent,
  widgetIdRef,
  handleIncrement,
  handleDecrement,
  setIsCheckoutPriceLoading,
  onRemoveEnrollmentPack,
  onEnrollmentPackAdd,
  alwaysOpen = false,
}) => {
  const { subscribeCartItem, unsubscribeCartItem } = window.FluidCommerceSDK;

  let checkoutButtonLabel = "$0.00";
  if (alwaysOpen && cart?.sub_total_in_currency) {
    checkoutButtonLabel = `${cart.sub_total_in_currency} - Add to Cart`;
  } else if (
    !isCheckoutPriceLoading &&
    !isCartLoading &&
    cart?.sub_total_in_currency
  ) {
    checkoutButtonLabel = `${cart.sub_total_in_currency} - Checkout`;
  }

  const enrollmentPackRemoval = async (pack: EnrollmentPack) => {
    const variantId = pack.firstItem.variant?.id ?? 0;

    if (onRemoveEnrollmentPack) {
      onRemoveEnrollmentPack();
      return;
    }

    // Set loading state
    setItemLoadingStates((prev) => ({
      ...prev,
      [variantId]: true,
    }));

    // If in demo mode, update state directly
    if (demoMode) {
      const updatedItems = cart?.items?.filter((i) => !i.enrollment);

      const updatedCart = {
        ...cart,
        items: updatedItems,
        recurring: cart?.recurring ?? [],
      };
      setCart(updatedCart);
      setItemCount(getTotalQuantityFromCart(updatedCart));

      dispatchCartEvent(
        "REMOVE_FROM_CART",
        {
          itemId: pack.firstItem.id,
          action: "remove-enrollment-pack",
          itemDetails: pack.firstItem,
          timestamp: new Date().toISOString(),
          demoMode: true,
        },
        widgetIdRef.current,
      );

      setTimeout(() => {
        setItemLoadingStates((prev) => ({
          ...prev,
          [variantId]: false,
        }));
      }, 300);
      return;
    }

    try {
      // Remove the entire enrollment pack using its ID
      // Use the first item's ID to remove the enrollment pack
      const itemId = pack.firstItem.id;
      if (itemId != null) {
        await removeCartItemById(itemId);
      }

      // Refresh cart from SDK
      const freshCart = await getCartWithFallback();
      if (freshCart) {
        setCart(freshCart);
        setItemCount(getTotalQuantityFromCart(freshCart));
        localStorage.setItem("cart_last_updated", Date.now().toString());
      }
    } catch (error) {
      console.error(
        `Cart widget [${widgetIdRef.current}]: Failed to remove enrollment pack:`,
        error,
      );
    } finally {
      setItemLoadingStates((prev) => ({
        ...prev,
        [variantId]: false,
      }));
    }
  };

  const getCartItemErrors = (
    item: Schemas["CartItem"],
  ): { type: ErrorBannerType | "default"; message?: string }[] => {
    const errors: { type: ErrorBannerType | "default"; message?: string }[] =
      [];
    if (Array.isArray(item.errors) && item.errors.length > 0) {
      for (const error of item.errors) {
        if (typeof error === "object" && error !== null) {
          if ("quantity" in error) {
            errors.push({ type: "out_of_stock" });
          } else if ("country" in error) {
            errors.push({ type: "not_available_in_country" });
          } else {
            const keys = Object.keys(error);
            if (keys.length === 1) {
              errors.push({
                type: "default",
                message: String(error[keys[0] as keyof typeof error]),
              });
            } else {
              errors.push({ type: "default", message: JSON.stringify(error) });
            }
          }
        } else if (typeof error === "string") {
          errors.push({ type: "default", message: error });
        } else {
          errors.push({ type: "default", message: String(error) });
        }
      }
    }
    return errors;
  };

  const handleSubscriptionChange = async (
    item: Schemas["CartItem"],
    checked: boolean,
    planId?: number,
  ) => {
    if (!item.id) return;
    setIsCheckoutPriceLoading(true);
    const savingsDisplay = item.subscribe_and_save_for_display ?? "";
    if (savingsDisplay && !item.subscription) {
      localStorage.setItem(`base_savings_${item.id}`, savingsDisplay);
    }
    if (demoMode) {
      setTimeout(() => {
        if (cart?.items) {
          const baseSavingsDisplay =
            localStorage.getItem(`base_savings_${item.id}`) ?? savingsDisplay;
          const updatedItems = cart.items.map((i) => {
            if (i.id === item.id) {
              let subscriptionPrice = i.subscription_price_in_currency;
              if (checked && !subscriptionPrice && i.price_in_currency) {
                const regularPriceMatch = /[\d.]+/.exec(i.price_in_currency);
                if (regularPriceMatch) {
                  const regularPrice = parseFloat(regularPriceMatch[0]);
                  const subscriptionPriceValue = (regularPrice * 0.8).toFixed(
                    2,
                  );
                  subscriptionPrice = i.price_in_currency.replace(
                    /[\d.]+/,
                    subscriptionPriceValue,
                  );
                }
              }
              return {
                ...i,
                subscription: !!checked,
                subscription_price_in_currency: checked
                  ? subscriptionPrice
                  : i.subscription_price_in_currency,
                subscribe_and_save_for_display: checked
                  ? baseSavingsDisplay
                  : undefined,
                subscription_plan_id: planId ?? i.subscription_plan_id,
              };
            }
            return i;
          });
          const updatedCart = {
            ...cart,
            items: updatedItems,
            recurring: cart.recurring,
          };
          setCart(updatedCart);
          setIsCheckoutPriceLoading(false);
        }
      }, 500);
      return;
    }
    dispatchCartEvent(
      "UPDATE_CART_ITEM",
      {
        itemId: item.id,
        action: item.subscription ? "unsubscribe" : "subscribe",
        itemDetails: item,
        timestamp: new Date().toISOString(),
        planId,
      },
      widgetIdRef.current,
    );
    try {
      let updatedCartData;
      if (!checked) {
        if (typeof item.id === "number") {
          updatedCartData = await unsubscribeCartItem(item.id);
        }
      } else {
        if (typeof item.id === "number") {
          updatedCartData = await subscribeCartItem(item.id, planId);
        }
      }
      if (updatedCartData) {
        const freshCart =
          "cart" in updatedCartData
            ? (updatedCartData.cart as Awaited<ReturnType<typeof getCart>>)
            : (updatedCartData as Awaited<ReturnType<typeof getCart>>);
        setCart(freshCart);
        setItemCount(getTotalQuantityFromCart(freshCart));
        localStorage.setItem("cart_last_updated", Date.now().toString());
      }
    } catch (error) {
      console.error(
        `Cart widget [${widgetIdRef.current}]: Failed to ${!checked ? "unsubscribe from" : "subscribe to"} item ${item.id}:`,
        error,
      );
    } finally {
      setIsCheckoutPriceLoading(false);
    }
  };

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-muted-foreground">Your cart from</p>
          <div className="text-[18px] font-bold text-foreground">
            {effectiveShopDisplayName}
          </div>
        </div>
      </div>
      {/* Checkout button - matching original design */}
      <Button
        onClick={handleCheckout}
        disabled={
          !cart?.items?.some((item) => item.quantity && item.quantity > 0) ||
          isCartLoading ||
          isCheckoutPriceLoading ||
          isAnyItemLoading
        }
        variant="default"
        size={"lg"}
        className="flex h-12 w-full items-center justify-center rounded-sm bg-primary font-semibold text-white sm:h-10"
        isLoading={
          (isCartLoading || isCheckoutPriceLoading) && !isAnyItemLoading
        }
      >
        {checkoutButtonLabel}
      </Button>
      <ScrollArea className="mt-4 flex-1 overflow-y-auto">
        {isCartLoading ? (
          <div className="flex h-full w-full items-center justify-center py-10 text-center text-muted-foreground">
            Loading cart...
          </div>
        ) : cart?.items?.length === 0 ? (
          <div className="px-4 py-10 text-center text-muted-foreground">
            Your cart is empty
          </div>
        ) : (
          <div>
            {/* Group items by enrollment pack */}
            {(() => {
              const { regularItems, enrollmentPacks } = groupCartItems(
                cart?.items,
                cart,
              );
              return (
                <>
                  {/* Render enrollment packs first */}
                  {enrollmentPacks.map(
                    (pack: EnrollmentPack, packIndex: number) => (
                      <div
                        key={`pack-${pack.id}`}
                        className={`mb-4 pb-4 ${
                          packIndex < enrollmentPacks.length - 1 ||
                          regularItems.length > 0
                            ? "border-b border-border"
                            : ""
                        }`}
                      >
                        {/* Enrollment Pack Header */}
                        <div className="mb-2 flex items-start">
                          {/* Pack Image */}
                          <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-sm">
                            <img
                              src={
                                pack.firstItem.variant?.image_url ??
                                pack.firstItem.product?.image_url ??
                                "https://placehold.co/60x60?text=Pack"
                              }
                              alt={pack.title}
                              className="h-full w-full object-cover"
                            />
                          </div>

                          {/* Pack Details */}
                          <div className="flex flex-1 flex-col px-3">
                            <div className="font-semibold text-foreground">
                              {pack.title}
                            </div>
                            <div className="-mt-0.5 mb-0.5 text-xs text-muted-foreground">
                              (Enrollment Pack)
                            </div>
                            <p className="text-sm font-semibold text-foreground">
                              {pack.price}
                            </p>
                          </div>

                          {/* Remove and add to cart button for entire pack */}
                          {pack.items.some(
                            (item: Schemas["CartItem"]) =>
                              item.quantity && item.quantity > 0,
                          ) ? (
                            <div className="ml-2 self-center">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 p-0"
                                isLoading={
                                  itemLoadingStates[
                                    pack.firstItem.variant?.id ?? 0
                                  ] ?? false
                                }
                                disabled={
                                  isAnyItemLoading ||
                                  (itemLoadingStates[
                                    pack.firstItem.variant?.id ?? 0
                                  ] ??
                                    false)
                                }
                                onClick={async () =>
                                  await enrollmentPackRemoval(pack)
                                }
                              >
                                <Trash2Icon className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : (
                            <div className="ml-2 self-center">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 p-0"
                                onClick={() => {
                                  if (onEnrollmentPackAdd) {
                                    onEnrollmentPackAdd();
                                  } else {
                                    console.log(
                                      "onEnrollmentPackAdd not found",
                                    );
                                  }
                                }}
                              >
                                <PlusIcon className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </div>

                        {/* Sub-items */}
                        <div
                          style={{ marginLeft: 24 }}
                          className="border-l border-border"
                        >
                          {pack.items.map(
                            (item: Schemas["CartItem"], itemIndex: number) => (
                              <div
                                key={`subitem-${item.id ?? `variant-${item.variant?.id}`}`}
                                className={`flex items-center px-4 py-2 ${
                                  itemIndex < pack.items.length - 1
                                    ? "border-b border-border"
                                    : ""
                                }`}
                              >
                                {/* Small thumbnail */}
                                <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-sm">
                                  <img
                                    src={
                                      item.variant?.image_url ??
                                      item.product?.image_url ??
                                      "https://placehold.co/60x60?text=Product"
                                    }
                                    alt={item.product_title ?? "Product"}
                                    className="h-full w-full object-cover"
                                  />
                                </div>

                                {/* Item title */}
                                <div className="flex-1 px-3">
                                  <div className="text-sm text-foreground">
                                    {item.product_title}
                                  </div>
                                </div>

                                {/* Quantity (only display if more than 1) */}
                                {Number(item.quantity) > 1 && (
                                  <div className="text-sm text-muted-foreground">
                                    x {Number(item.quantity)}
                                  </div>
                                )}
                              </div>
                            ),
                          )}
                        </div>
                      </div>
                    ),
                  )}

                  {/* Render regular items after enrollment packs */}
                  {regularItems.map((item, index) => {
                    const errorTypes = getCartItemErrors(item);
                    return (
                      <div
                        key={item.id ?? `my-4 variant-${item.variant?.id}`}
                        className={`${
                          index < regularItems.length - 1
                            ? "mb-4 border-b border-border"
                            : ""
                        } ${errorTypes.length > 0 ? "pb-0" : "pb-4"}`}
                      >
                        <div className="flex">
                          {/* Product Image */}
                          <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-sm">
                            <img
                              src={
                                item.variant?.image_url ??
                                item.product?.image_url ??
                                "https://placehold.co/60x60?text=Product"
                              }
                              alt={item.product_title ?? "Product"}
                              className="h-full w-full object-cover"
                            />
                          </div>

                          {/* Product Details */}
                          <div className="flex flex-1 flex-col px-3">
                            <div className="font-semibold text-foreground">
                              {item.product_title}
                            </div>
                            <p className="mt-0.5 text-sm font-semibold text-foreground">
                              {/* Display subscription price when item is subscribed and has subscription price */}
                              {(() => {
                                const hasSubscription = Boolean(
                                  item.subscription,
                                );
                                const hasSubscriptionPrice = Boolean(
                                  item.subscription_price_in_currency,
                                );
                                const regularPrice = item.price_in_currency;
                                const subscriptionPrice =
                                  item.subscription_price_in_currency;

                                // Show subscription price if item is subscribed AND has a subscription price
                                // Otherwise fall back to regular price
                                const displayPrice =
                                  hasSubscription && hasSubscriptionPrice
                                    ? subscriptionPrice
                                    : regularPrice;

                                return displayPrice;
                              })()}

                              {item.subscription && item.price_in_currency && (
                                <span className="ml-2 text-xs text-muted-foreground line-through">
                                  {(() => {
                                    const price = parseFloat(
                                      String(
                                        item.subscription_price ?? "",
                                      ).replace(/[^\d.]/g, ""),
                                    );
                                    const save = parseFloat(
                                      String(
                                        item.subscribe_and_save ?? "",
                                      ).replace(/[^\d.]/g, ""),
                                    );
                                    if (isNaN(price) || isNaN(save))
                                      return item.price_in_currency;
                                    // preserve currency symbol if present
                                    const currencySymbol = (
                                      item.price_in_currency ?? ""
                                    ).replace(/[\d.,\s]/g, "");
                                    return `${currencySymbol}${(price + save).toFixed(2)}`;
                                  })()}
                                </span>
                              )}
                            </p>

                            {/* Subscription toggle */}
                            {item.allow_subscription && (
                              <div className="ml-2 mt-1">
                                <CartSubscriptions
                                  item={item}
                                  onPlanChange={(item, checked, planId) =>
                                    handleSubscriptionChange(
                                      item,
                                      checked,
                                      planId,
                                    )
                                  }
                                />
                              </div>
                            )}
                          </div>
                          {/* Quantity controls for regular items */}
                          <div className="ml-2 self-center">
                            <QuantityModifier
                              quantity={Number(item.quantity) || 0}
                              onIncrement={() =>
                                handleIncrement(item.variant?.id ?? 0)
                              }
                              onRemove={() =>
                                handleDecrement(item.variant?.id ?? 0)
                              }
                              disabled={
                                isAnyItemLoading &&
                                !itemLoadingStates[item.variant?.id ?? 0]
                              }
                              isLoading={
                                itemLoadingStates[item.variant?.id ?? 0] ??
                                false
                              }
                            />
                          </div>
                        </div>
                        {/* Error Banner for this item, full width below item UI */}
                        {errorTypes.map((err, i) => (
                          <div
                            key={err.type + (err.message ?? "") + i}
                            className="mt-2 w-full"
                          >
                            {err.type === "default" ? (
                              <ErrorBanner
                                type="default"
                                message={err.message}
                              />
                            ) : (
                              <ErrorBanner type={err.type} />
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </>
              );
            })()}
          </div>
        )}
      </ScrollArea>
    </div>
  );
};

export default CartSidebarContent;
